import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DefaultStatus, Product, ProductStockCount, SiteProduct } from '../models/response-models/product';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { UntypedFormControl, UntypedFormGroup } from '@angular/forms';
import { ProductCategoryDisplay } from '../models/response-models/product-category-display';
import { Unit } from '../models/response-models/unit';
import { PriceOptions } from '../models/application-models/price-options';
import { RelationshipListElement } from '../models/application-models/relationship-list-element';
import { MultiSelectionIntItem } from 'src/app/models/application-models/multi-selection-int-item';
import { MultiSelectionItem } from '../models/application-models/multi-selection-item';

@Injectable({
  providedIn: 'root'
})
export class ProductService {
  searchedText: string;
  previousProductFilter: Product[] = [];
  previousCategoryFilter: MultiSelectionItem[] = [];
  productPaginatorPage: number;

  editActions: MultiSelectionIntItem[] = [
    { id: 1, itemName:'Vat Change' },
    { id: 2, itemName:'Settings Change' },
    { id: 3, itemName:'Routing Tag Change' }];

  showActiveOnly: UntypedFormControl = new UntypedFormControl();
  isComboDeal: UntypedFormControl = new UntypedFormControl();

  constructor(
    private http: HttpClient
  ) { }

  getAllProducts(): Observable<Product[]>{
    return this.http.get<Product[]>(environment.serverUrl + "/products");
  }

  getProductByID(productID: string): Observable<Product>{
    return this.http.get<Product>(environment.serverUrl + `/products/${productID}`);
  }

  getUnits(): Observable<Unit[]>{
    return this.http.get<Unit[]>(environment.serverUrl + `/products/units`);
  }

  getNextPlu(): Observable<number>{
    return this.http.get<number>(environment.serverUrl + `/products/nextPLU`);
  }

  createProduct(form: UntypedFormGroup): Observable<Product>{
    let options = {
      PLU: form.controls.pluCode.value,
      costPrice: parseFloat(form.controls.costPrice.value),
      course: form.controls.course.value?.id,
      courseID: form.controls.course.value?.id,
      isCourseInherited: form.controls.isCourseInherited.value,
      description: form.controls.description.value,
      displayName: !form.controls.posDisplayName.value ? form.controls.defaultName.value : form.controls.posDisplayName.value,
      excludeFromReceipt: form.controls.excludeFromReceipt.value,
      defaultStatus: form.controls.defaultHoldStatus.value ? DefaultStatus.ONHOLD : DefaultStatus.OPEN,
      grossProfit: parseFloat(form.controls.grossProfit.value),
      grossProfitMargin: form.controls.grossProfitMargin.value,
      id: form.controls.id.value,
      index: form.controls.index.value,
      isActive: form.controls.isActive.value,
      kdsCookTime: parseFloat(form.controls.kdsCookTime.value),
      kdsID: parseFloat(form.controls.kdsID.value),
      kdsIsPercent: form.controls.kdsIsPercentage.value,
      kdsNewTime: form.controls.kdsNewTime.value,
      kdsRushTime: form.controls.kdsRushTime.value,
      name: form.controls.defaultName.value,
      orderingName: !form.controls.preparationName.value ? form.controls.defaultName.value : form.controls.preparationName.value,
      price: parseFloat(form.controls.defaultPrice.value),
      productCategoryID: form.controls.category.value,
      promptForAgeVerification: form.controls.promptForAgeVerification.value,
      priceOverride: form.controls.priceOverride.value,
      promptForPrice: form.controls.promptForPrice.value,
      promptForUnit: form.controls.promptForUnit.value,
      receiptName: !form.controls.receiptName.value ? form.controls.defaultName.value : form.controls.receiptName.value,
      sellOnPOS: form.controls.sellOnPos.value,
      unitID: form.controls.unit.value,
      vatRateID: form.controls.vatRate.value?.id,
      imageUrl: form.controls.imageUrl.value,
      isComboDeal: form.controls.isComboDeal.value,
      isPriceByBOGOF: form.controls.isPriceByBOGOF.value,
      stagingAreaID: form.controls.stagingAreaID?.value
    }

    return this.http.post<Product>(environment.serverUrl + `/products`, options);
  }

  editProduct(form: UntypedFormGroup): Observable<Product>{
    let options = {
      PLU: form.controls.pluCode.value,
      costPrice: parseFloat(form.controls.costPrice.value),
      course: form.controls.course.value?.id,
      courseID: form.controls.course.value?.id,
      isCourseInherited: form.controls.isCourseInherited.value,
      description: form.controls.description.value,
      displayName: !form.controls.posDisplayName.value ? form.controls.defaultName.value : form.controls.posDisplayName.value,
      excludeFromReceipt: form.controls.excludeFromReceipt.value,
      defaultStatus: form.controls.defaultHoldStatus.value ? DefaultStatus.ONHOLD : DefaultStatus.OPEN,
      grossProfit: parseFloat(form.controls.grossProfit.value),
      grossProfitMargin: form.controls.grossProfitMargin.value,
      id: form.controls.id.value,
      index: form.controls.index.value,
      isActive: form.controls.isActive.value,
      kdsCookTime: parseFloat(form.controls.kdsCookTime.value),
      kdsID: parseFloat(form.controls.kdsID.value),
      kdsIsPercent: form.controls.kdsIsPercentage.value,
      kdsNewTime: form.controls.kdsNewTime.value,
      kdsRushTime: form.controls.kdsRushTime.value,
      name: form.controls.defaultName.value,
      orderingName: !form.controls.preparationName.value ? form.controls.defaultName.value : form.controls.preparationName.value,
      price: parseFloat(form.controls.defaultPrice.value),
      productCategoryID: form.controls.category.value,
      promptForAgeVerification: form.controls.promptForAgeVerification.value,
      priceOverride: form.controls.priceOverride.value,
      promptForPrice: form.controls.promptForPrice.value,
      promptForUnit: form.controls.promptForUnit.value,
      receiptName: !form.controls.receiptName.value ? form.controls.defaultName.value : form.controls.receiptName.value,
      sellOnPOS: form.controls.sellOnPos.value,
      unitID: form.controls.unit.value,
      vatRateID: form.controls.vatRate.value?.id,
      imageUrl: form.controls.imageUrl.value,
      isComboDeal: form.controls.isComboDeal.value,
      isPriceByBOGOF: form.controls.isPriceByBOGOF.value,
      stagingAreaID: form.controls.stagingAreaID?.value
    }

    return this.http.put<Product>(environment.serverUrl + `/products`, options);
  }

  editProductDescription(form: UntypedFormGroup, product: Product): Observable<Product>{
    let options = {
      PLU: product.plu,
      costPrice: product.costPrice,
      course: product.course,
      courseID: product.course?.id,
      description: form.controls.description.value,
      displayName: product.displayName,
      excludeFromReceipt: product.excludeFromReceipt,
      grossProfit: product.grossProfit,
      grossProfitMargin: product.grossProfitMargin,
      id: product.id,
      index: product.index,
      isActive: product.isActive,
      kdsCookTime: product.kdsCookTime,
      kdsID: product.kdsID,
      kdsIsPercent: product.kdsIsPercent,
      kdsNewTime: product.kdsNewTime,
      kdsRushTime: product.kdsRushTime,
      name: product.name,
      orderingName: product.orderingName,
      price: product.price,
      productCategoryID: product.productCategory.id,
      promptForAgeVerification: product.promptForAgeVerification,
      priceOverride: product.priceOverride,
      promptForPrice: product.promptForPrice,
      promptForUnit: product.promptForUnit,
      receiptName: product.receiptName,
      sellOnPOS: product.sellOnPOS,
      unitID: product.unitID,
      vatRateID: product.vatRate?.id,
      imageUrl: product.imageUrl,
      isComboDeal: product.isComboDeal,
      isPriceByBOGOF: product.isPriceByBOGOF
    };

    return this.http.put<Product>(environment.serverUrl + `/products`, options);
  }

  createProductDisplay(form: UntypedFormGroup): Observable<ProductCategoryDisplay>{
    let id = form.controls.id.value;

    let options = {
      borderColour: form.controls.borderColor.value,
      borderRadius: form.controls.borderRadius.value,
      borderStyle: form.controls.borderStyle.value,
      borderWeight: form.controls.borderWeight.value,
      fillCSS: form.controls.backgroundColor.value,
      fillType: form.controls.buttonFill.value,
      font: form.controls.fontType.value,
      fontColour: form.controls.fontColor.value,
      fontIsBold: form.controls.isBold.value,
      fontIsItalic: form.controls.isItalic.value,
      fontIsUnderline: form.controls.isUnderlined.value,
      fontSize: form.controls.fontSize.value + "pt",
      id: id
    };

    return this.http.post<ProductCategoryDisplay>(environment.serverUrl + `/products/${id}/productDisplay`, options);
  }

  editProductDisplay(form: UntypedFormGroup): Observable<ProductCategoryDisplay>{
    let id = form.controls.id.value;

    let options = {
      borderColour: form.controls.borderColor.value,
      borderRadius: form.controls.borderRadius.value,
      borderStyle: form.controls.borderStyle.value,
      borderWeight: form.controls.borderWeight.value,
      fillCSS: form.controls.backgroundColor.value,
      fillType: form.controls.buttonFill.value,
      font: form.controls.fontType.value,
      fontColour: form.controls.fontColor.value,
      fontIsBold: form.controls.isBold.value,
      fontIsItalic: form.controls.isItalic.value,
      fontIsUnderline: form.controls.isUnderlined.value,
      fontSize: form.controls.fontSize.value + "pt",
      id: id
    };

    return this.http.put<ProductCategoryDisplay>(environment.serverUrl + `/products/${id}/productDisplay`, options);
  }

  manageModifierGroupByID(productID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/products/${productID}/modifierGroups`, options);
  }

  manageRoutingTagByID(productID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/products/${productID}/routeTags`, options);
  }

  manageOrderTypeByID(productID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/products/${productID}/orderTypes`, options);
  }

  manageDisplayOnlyCategoryByID(productID: string, relationshipList: RelationshipListElement[]){
    let options = relationshipList;

    return this.http.post<any>(environment.serverUrl + `/products/${productID}/productCategories`, options);
  }

  removeProductByID(productID: string){
    let options: any = {
      body: {
        id: productID
      }
    };

    return this.http.delete<any>(environment.serverUrl + `/products`, options);
  }

  uploadImage(file: File, productID: string){
    const formData = new FormData();
    formData.append('file', file);

		return this.http.post<any>(environment.serverUrl + `/resources/files/product/${productID.toUpperCase()}`, formData);
  }

  modifyProductPrice(options: PriceOptions, productID: string): Observable<Product>{
    return this.http.patch<Product>(environment.serverUrl + `/products/${productID}`, options);
  }

  modifySiteProductByOrderType(siteProducts: SiteProduct[], productID: string): Observable<SiteProduct[]>{
    return this.http.put<SiteProduct[]>(environment.serverUrl + `/products/${productID}/SiteProducts`, siteProducts);
  }

  clearSearchFilters(){
    this.previousCategoryFilter = [];
    this.previousProductFilter = [];
    this.searchedText = '';
    this.productPaginatorPage = 1;
    this.showActiveOnly.setValue(false);
    this.isComboDeal.setValue(false);
  }

  deleteBulkProducts(productIds: string[]){
    let options: any = {
      body: productIds
    };

    return this.http.delete<any>(environment.serverUrl + `/products/bulkDelete`, options);
  }

  snoozeBulkProducts(productIds: string[]){
    return this.http.put<any>(environment.serverUrl + `/products/bulkSnooze`, productIds);
  }

  bulkProductsUpdate(action: number, products: Product[]){
    return this.http.post<any>(environment.serverUrl + `/products/${action}/bulkUpdate`, products)
  }

  getSiteProducts(siteId: string){
    return this.http.get<SiteProduct[]>(environment.serverUrl + `/products/siteProducts/${siteId}`)
  }

  getSiteProductsForUser(userId: string) {
    return this.http.get<Product[]>(environment.serverUrl + `/products/siteProducts/user/${userId}`)
  }

  getAllProductStock(){
    return this.http.get<ProductStockCount[]>(environment.serverUrl + `/products/productStockCount`)
  }

  updateProductStockCount(productStock: ProductStockCount){
    return this.http.put<ProductStockCount>(environment.serverUrl + `/products/siteProducts`, productStock)
  }

  removeProductStockCount(productStock: ProductStockCount){

    let options: any = {
      body: productStock
    };
    return this.http.delete<ProductStockCount>(environment.serverUrl + `/products/siteProducts/remove`, options)
  }
}
